import * as React from 'react';
import { applyRunwayTheme } from './globalStyles';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'runway.themeMode';

function getInitialThemeMode(): ThemeMode {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }
    } catch {
        // ignore (storage may be unavailable)
    }

    // Don't use matchMedia to determine the initial theme mode yet
    // if (typeof window !== 'undefined' && window.matchMedia) {
    //     return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    // }

    return 'light';
}

type ThemeModeContextValue = {
    mode: ThemeMode;
    setMode: (mode: ThemeMode) => void;
    toggleMode: () => void;
};

const defaultContextValue: ThemeModeContextValue = {
    mode: 'light',
    setMode: () => {},
    toggleMode: () => {},
};

const ThemeModeContext = React.createContext<ThemeModeContextValue>(defaultContextValue);

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setMode] = React.useState<ThemeMode>(() => getInitialThemeMode());

    React.useEffect(() => {
        document.documentElement.setAttribute('data-theme', mode);
        applyRunwayTheme(mode);

        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // ignore
        }
    }, [mode]);

    const toggleMode = React.useCallback(() => {
        setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
    }, []);

    return <ThemeModeContext.Provider value={{ mode, setMode, toggleMode }}>{children}</ThemeModeContext.Provider>;
};

export function useThemeMode(): ThemeModeContextValue {
    return React.useContext(ThemeModeContext);
}

export function useChartTheme(): 'dark' | 'default' {
    const { mode } = useThemeMode();
    return mode === 'dark' ? 'dark' : 'default';
}
export interface ChartColors {
    gridLine: string;
    axisLabel: string;
    annotationText: string;
    tooltipBg: string;
    tooltipText: string;
    secondarySeries: string;
}

const CHART_COLORS_LIGHT: ChartColors = {
    gridLine: '#f0f0f0', // antGrey.grey4
    axisLabel: '#8c8c8c', // antGrey.grey7
    annotationText: '#595959', // antGrey.grey8
    tooltipBg: '#595959', // antGrey.grey8
    tooltipText: '#ffffff',
    secondarySeries: '#bfbfbf', // antGrey.grey6
};

const CHART_COLORS_DARK: ChartColors = {
    gridLine: '#303030', // dark mode grey4 equivalent
    axisLabel: '#bfbfbf', // dark mode grey7 equivalent
    annotationText: '#d9d9d9', // dark mode grey8 equivalent
    tooltipBg: '#262626', // dark mode grey8 equivalent
    tooltipText: '#f5f5f5',
    secondarySeries: '#595959', // dark mode grey6 equivalent
};

export function useChartColors(): ChartColors {
    const { mode } = useThemeMode();
    return mode === 'dark' ? CHART_COLORS_DARK : CHART_COLORS_LIGHT;
}
